import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/client";
import get from "lodash/get";
import classnames from "classnames";

import ProductInfo from "apps/payment/components/ProductInfo";
import PaymentFormSubmitButton from "apps/payment/components/PaymentFormSubmitButton";
import LegalText from "apps/payment/components/LegalText";
import Str from "shared/components/Str";
import Strf from "shared/components/Strf";
import { PRODUCT_SUBTYPES } from "shared/payment/constants";
import {
	PAYPAL_URL_QUERY,
} from "apps/payment/containers/AndroidPayment/queries";
import { getPlatform } from "shared/payment/services";
import getPaypalCallbacks from "shared/payment/helpers/getPaypalCallbacks";
import Context from "shared/util/Context";
import {
	selectedPaymentTypeOnCheckoutEvent,
} from "shared/payment/analytics";
import "./OneClickPurchase.scss";

const OneClickPurchase = ({
	productName,
	taxAmount,
	productPrice,
	productSubtype,
	totalPrice,
	formattedInitialPriceWithoutDiscount,
	storedCreditCardLastFour,
	onPaymentMethodChange,
	onSubmit,
	isPayPalScreen,
	productId,
	hasVATIncluded,
	isInternational,
	convertedProductPrice,
	rateCardName,
	productType,
	paymentOptions,
	currency,
	subscriptionSubPeriod,
	tokensCount,
	subPeriodType,
	isUserInWashingtonState,
	isProcessing,
	hasSuccessfullyPurchased,
	hasSuccessfullyAuthedPayPal,
	upgradePrice,
}) => {
	const platform = getPlatform();
	const callbacks = getPaypalCallbacks({
		productId,
		...(window.location || {}),
		platform,
	});


	useEffect(() => {
		// analytics
		selectedPaymentTypeOnCheckoutEvent({
			upgradePrice,
			rateCardName,
			isDefaultPaymentType: true,
			isSavedCreditCard: true,
			paymentType: "cc",
			productSubtype,
			productType,
			paymentOptions,
			currency,
			subscriptionSubPeriod,
			tokensCount,
			unitPrice: productPrice,
			subPeriodType,
		});
		// eslint-disable-next-line
	}, []);

	const {
		data,
	} = useQuery(PAYPAL_URL_QUERY, {
		variables: {
			userId: Context.get("userid"),
			platform,
			productId,
			returnURL: `${callbacks}&paypalsuccess=1`,
			cancelURL: `${callbacks}&paypalcancel=1`,
		},
		skip: hasSuccessfullyAuthedPayPal,
	});

	const payPalURL = get(data, "user.payPalURL");

	const ctaComponent = isPayPalScreen && payPalURL
		? (
			<div className="one-click-purchase-paypal-url-container">
				<LegalText productSubtype={productSubtype} isPayPalScreen={isPayPalScreen} />
				<button
					className={classnames("one-click-purchase-paypal-url", {
						"one-click-purchase-paypal-url-success": hasSuccessfullyPurchased,
					})}
					onClick={() => {
						window.location.href = payPalURL;
					}}
					type="button"
				>
					<Str>Continue to PayPal</Str>
				</button>
			</div>
		)
		: (
			<PaymentFormSubmitButton
				productSubtype={productSubtype}
				isPayPalScreen={isPayPalScreen}
				isProcessing={isProcessing}
				hasSuccessfullyPurchased={hasSuccessfullyPurchased}
			/>
		);

	return (
		<form
			onSubmit={(evt) => {
				evt.preventDefault();
				onSubmit();
			}}
			className="one-click-purchase"
		>
			<div className="one-click-puchase-info-container">
				<Str
					className="one-click-purchase-pay-with"
				>
					Pay With
				</Str>
				<div className="one-click-purchase-stored-cc">
					{!isPayPalScreen
						? (
							<React.Fragment>
								<Strf
									vars={{
										storedCreditCardLastFour,
									}}
								>
									Card ending in *$storedCreditCardLastFour
								</Strf>
								<button
									className="one-click-purchase-change-btn"
									onClick={onPaymentMethodChange}
									type="button"
								>
									<Str>Change</Str>
								</button>
							</React.Fragment>
						)
						: <Str>PayPal</Str>
					}
				</div>
				<ProductInfo
					productName={productName}
					taxAmount={taxAmount}
					productPrice={productPrice}
					productSubtype={productSubtype}
					totalPrice={totalPrice}
					hasVATIncluded={hasVATIncluded}
					isInternational={isInternational}
					convertedProductPrice={convertedProductPrice}
					isUserInWashingtonState={isUserInWashingtonState}
					formattedInitialPriceWithoutDiscount={formattedInitialPriceWithoutDiscount}
				/>
			</div>
			{ctaComponent}
		</form>
	);
};

OneClickPurchase.propTypes = {
	productName: PropTypes.string.isRequired,
	taxAmount: PropTypes.string.isRequired,
	productPrice: PropTypes.string.isRequired,
	productSubtype: PropTypes.oneOf(Object.values(PRODUCT_SUBTYPES)).isRequired,
	totalPrice: PropTypes.string.isRequired,
	storedCreditCardLastFour: PropTypes.string.isRequired,
	onPaymentMethodChange: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
	isPayPalScreen: PropTypes.bool,
	productId: PropTypes.string.isRequired,
	hasVATIncluded: PropTypes.bool,
	isInternational: PropTypes.bool,
	convertedProductPrice: PropTypes.string,
	rateCardName: PropTypes.string.isRequired,
	productType: PropTypes.string.isRequired,
	paymentOptions: PropTypes.array.isRequired,
	currency: PropTypes.string.isRequired,
	subscriptionSubPeriod: PropTypes.string,
	tokensCount: PropTypes.number,
	subPeriodType: PropTypes.string,
	isUserInWashingtonState: PropTypes.bool,
	isProcessing: PropTypes.bool,
	hasSuccessfullyPurchased: PropTypes.bool,
	hasSuccessfullyAuthedPayPal: PropTypes.bool,
	upgradePrice: PropTypes.number,
	formattedInitialPriceWithoutDiscount: PropTypes.string,
};

OneClickPurchase.defaultProps = {
	isPayPalScreen: false,
	hasVATIncluded: false,
	isInternational: false,
	convertedProductPrice: null,
	subscriptionSubPeriod: null,
	tokensCount: null,
	subPeriodType: null,
	isUserInWashingtonState: false,
	isProcessing: false,
	hasSuccessfullyPurchased: false,
	hasSuccessfullyAuthedPayPal: false,
	upgradePrice: null,
	formattedInitialPriceWithoutDiscount: null,
};

export default OneClickPurchase;



// WEBPACK FOOTER //
// ./src/apps/payment/components/OneClickPurchase/OneClickPurchase.jsx